import React, { useMemo, useState } from 'react';
import { IDays } from '@app/common';
import {
  CapacityMetric,
  CapacitySection,
  CopyMode,
  getCopyMismatch,
  OpenStarts,
  WEEK_DAYS,
} from './production-capacity-editor.helpers';
import { backdrop, colors, dialog, ghostButton, iconButton, primaryButton, sectionLabel } from './editor.styles';
import { CloseIcon, WarningIcon } from './icons';
import { dayLabel } from './CapacityGrid';

interface CopyDayDialogProps {
  source: IDays;
  metrics: CapacityMetric[];
  openStarts: OpenStarts;
  sections: CapacitySection[];
  onConfirm: (options: { to: IDays[]; metrics: string[]; mode: CopyMode }) => void;
  onClose: () => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count > 1 ? many : one}`;

const listFr = (items: string[]) => (
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`
);

const shortHours = (sectionNames: string[]) => (sectionNames.length ? listFr(sectionNames) : 'Fermé');

const checkbox = { width: 18, height: 18, margin: 0, accentColor: colors.control, flexShrink: 0 };

export const CopyDayDialog = ({ source, metrics, openStarts, sections, onConfirm, onClose }: CopyDayDialogProps) => {
  const [targets, setTargets] = useState<IDays[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(metrics.map((metric) => metric.key));
  const [mode, setMode] = useState<CopyMode>('replace');

  const sourceLabel = dayLabel(source).toLowerCase();

  const openDays = WEEK_DAYS.filter((day) => day !== source && openStarts[day].size > 0);

  const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

  const warnings = useMemo(() => targets.flatMap((day) => {
    const { ignored, unchanged } = getCopyMismatch(openStarts, source, day, sections);
    const label = dayLabel(day);
    const messages: string[] = [];
    const ignoredCount = ignored.reduce((sum, entry) => sum + entry.count, 0);

    if (ignoredCount) {
      const parts = ignored.map((entry) => `${plural(entry.count, 'créneau', 'créneaux')} ${entry.name.toLowerCase()}`);
      messages.push(`${label} : ${listFr(parts)} ${ignoredCount > 1 ? 'n’existent pas ce jour-là et seront ignorés' : 'n’existe pas ce jour-là et sera ignoré'}.`);
    }

    if (unchanged) {
      messages.push(`${label} : ${plural(unchanged, 'créneau ouvert', 'créneaux ouverts')} sans équivalent le ${sourceLabel} ${unchanged > 1 ? 'restent inchangés' : 'reste inchangé'}.`);
    }

    return messages;
  }), [targets, openStarts, source, sections, sourceLabel]);

  const sectionNamesOf = (day: IDays) => [...new Set(
    sections.filter((section) => section.starts.some((start) => openStarts[day].has(start))).map((section) => section.name.toLowerCase()),
  )].map((name, index) => (index === 0 ? name.charAt(0).toUpperCase() + name.slice(1) : name));

  const cannotCopy = !targets.length || !selectedMetrics.length;

  return (
    <div style={backdrop} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="capacity-copy-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.key === 'Escape' && onClose()}
        style={{ ...dialog, width: 600, gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h2 id="capacity-copy-title" style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Copier la capacité du {sourceLabel}</h2>
            <span style={{ fontSize: 14, color: colors.muted }}>Les limites du {sourceLabel} sont recopiées créneau par créneau sur les jours choisis.</span>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} style={{ ...iconButton, flexShrink: 0 }}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={sectionLabel}>Vers</span>
            <button type="button" onClick={() => setTargets(openDays)} style={{ padding: '4px 0', background: 'none', border: 'none', color: colors.accent, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Tous les jours ouverts
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
            {WEEK_DAYS.map((day) => {
              const isSource = day === source;
              const isDisabled = isSource || openStarts[day].size === 0;
              const isChecked = targets.includes(day);

              return (
                <label
                  key={day}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 56, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 10, cursor: isDisabled ? 'default' : 'pointer', opacity: isDisabled ? 0.5 : 1, ...(isChecked ? { background: 'rgba(0,137,200,0.16)', border: `1px solid ${colors.control}` } : { background: 'transparent', border: '1px solid rgba(253,255,252,0.14)' }) }}>
                  <input type="checkbox" checked={isChecked} disabled={isDisabled} onChange={() => setTargets(toggle(targets, day))} style={checkbox} />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{dayLabel(day)}</span>
                    <span style={{ fontSize: 11, color: colors.muted }}>{isSource ? 'Jour copié' : shortHours(sectionNamesOf(day))}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={sectionLabel}>Limites à copier</span>
            {metrics.map((metric) => (
              <label key={metric.key} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 32, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={selectedMetrics.includes(metric.key)} onChange={() => setSelectedMetrics(toggle(selectedMetrics, metric.key))} style={checkbox} />
                {metric.label}
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={sectionLabel}>Créneaux déjà remplis</span>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minHeight: 32, fontSize: 14, cursor: 'pointer' }}>
              <input type="radio" name="capacity-copy-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} style={{ ...checkbox, marginTop: 1 }} />
              <span>Remplacer par les valeurs copiées</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minHeight: 32, fontSize: 14, cursor: 'pointer' }}>
              <input type="radio" name="capacity-copy-mode" checked={mode === 'fill'} onChange={() => setMode('fill')} style={{ ...checkbox, marginTop: 1 }} />
              <span>Les garder, ne remplir que les créneaux sans limite</span>
            </label>
          </div>
        </div>

        {warnings.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderRadius: 10, background: 'rgba(232,186,33,0.10)', border: '1px solid rgba(232,186,33,0.30)' }}>
            {warnings.map((warning) => (
              <div key={warning} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, color: colors.warningText }}>
                <span style={{ flexShrink: 0, marginTop: 2, display: 'flex' }}><WarningIcon /></span>
                <span>{warning}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" onClick={onClose} style={{ ...ghostButton, height: 44, padding: '0 20px', fontSize: 15 }}>Annuler</button>
          <button
            type="button"
            disabled={cannotCopy}
            onClick={() => onConfirm({ to: targets, metrics: selectedMetrics, mode })}
            style={{ ...primaryButton, height: 44, padding: '0 24px', fontSize: 15, opacity: cannotCopy ? 0.45 : 1, cursor: cannotCopy ? 'not-allowed' : 'pointer' }}>
            {targets.length ? `Copier vers ${plural(targets.length, 'jour', 'jours')}` : 'Choisissez des jours'}
          </button>
        </div>
      </div>
    </div>
  );
};
